import React, { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Copy, FilePlus2, History, LifeBuoy, Mic, MoreHorizontal, Pencil, Plus, RefreshCw, Search, Send, Sparkles, Square, ThumbsDown, ThumbsUp, Trash2, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createConversation,
  createSupportTicket as createSupportTicketRequest,
  deleteConversation,
  draftSupportTicket,
  getConversation,
  getCurrentConversation,
  listConversations,
  renameConversation,
  saveAssistantFeedback,
  sendAssistantMessage,
  transcribeAssistantAudio,
} from "./productAssistant.api";
import {
  LOGAN_WORKFLOW_ERROR_EVENT,
  OPEN_LOGAN_SUPPORT_TICKET_EVENT,
  notifyLoganSupportTicketCreated,
} from "./events";
import { AssistantConversationSummary, AssistantMessage, AssistantReply } from "./types";
import decodeJwt from "helpers/jwt-token-access/jwtdecode";
import { getAssistantExperience } from "./assistantExperience";
import { useBillingAccess } from "components/BillingAccessContext";
import { hasContractPermission } from "Common/permissions";
import { PERMISSIONS } from "Common/constants/permissions";
import "./ProductAssistantWidget.css";

interface ProductAssistantWidgetProps {
  variant?: "floating" | "page";
}

const ProductAssistantWidget: React.FC<ProductAssistantWidgetProps> = ({ variant = "floating" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state: billingAccess, openPaymentRecovery } = useBillingAccess();
  const isPage = variant === "page";
  const isLeadWorkspace = ["/pages-projects", "/project-requested", "/project-ongoing-leads", "/leadHistory"].includes(location.pathname);
  const experience = useMemo(() => getAssistantExperience(decodeJwt()), []);
  const welcomeMessage = experience.welcomeMessage;
  const brandName = experience.brandName;
  const [open, setOpen] = useState(isPage);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([welcomeMessage]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [feedback, setFeedback] = useState<Record<string, "helpful" | "not_helpful">>({});
  const [ticketStep, setTicketStep] = useState<"form" | "confirm" | null>(null);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [ticketDrafting, setTicketDrafting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(() => isPage
    && typeof window !== "undefined"
    && (typeof window.matchMedia !== "function" || window.matchMedia("(min-width: 768px)").matches));
  const [historySearch, setHistorySearch] = useState("");
  const [conversations, setConversations] = useState<AssistantConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyMenuId, setHistoryMenuId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState("New conversation");
  const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [workflowHint, setWorkflowHint] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [projectGateMessage, setProjectGateMessage] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const streamTimerRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const pageContext = useMemo(
    () => getPageContext(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const lastSuggestions = [...messages].reverse().find((message) => message.suggestions?.length)?.suggestions || [];
  const contextualSuggestion = getContextualSuggestion(pageContext.module, experience.roleLabel);
  const visibleSuggestions = getVisibleSuggestions(
    [contextualSuggestion, ...lastSuggestions],
    experience.roleLabel
  );
  const isWelcomeState = isPage && !loading && messages.length === 1 && messages[0].id === "welcome";

  const openContractWorkspace = (prompt = "") => {
    if (billingAccess?.accessLevel === "suspended") {
      setProjectGateMessage("Resolve the billing issue before starting a new project contract.");
      openPaymentRecovery();
      return;
    }
    if (!hasContractPermission(PERMISSIONS.CREATE_CONTRACT)) {
      setProjectGateMessage("You do not have permission to create project contracts. Ask your company owner or administrator for access.");
      return;
    }
    setProjectGateMessage("");
    closeHistory();
    const context = [...messages
      .filter((message) => message.role === "user" && !message.failed)
      .map((message) => message.message), prompt]
      .map((message) => message.trim())
      .filter(Boolean)
      .filter((message, index, all) => all.indexOf(message) === index)
      .join("\n\n")
      .slice(-4000);
    navigate("/create-contract?type=project&logan=chat", {
      state: {
        focusLoganChat: true,
        loganContractContext: context,
      },
    });
  };

  useEffect(() => {
    if (!open || initialized) return;
    let active = true;
    setLoading(true);
    getCurrentConversation()
      .then(async (conversation) => {
        if (!active) return;
        if (conversation) {
          setConversationId(conversation.id);
          setConversationTitle(conversation.title);
          setMessages(conversation.messages.length ? conversation.messages : [welcomeMessage]);
        } else {
          const created = await createConversation(getLocale(), brandName);
          if (!active) return;
          setConversationId(created.id);
          setConversationTitle("New conversation");
          setMessages([welcomeMessage]);
        }
      })
      .catch(() => {
        if (active) setMessages([{ ...welcomeMessage, message: "Logan could not be loaded. Please try again.", failed: true }]);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
          setInitialized(true);
        }
      });
    return () => { active = false; };
  }, [open, initialized, brandName, welcomeMessage]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    const openTicketForm = () => {
      setOpen(true);
      setTicketStep("form");
      setTicketError("");
    };
    window.addEventListener(OPEN_LOGAN_SUPPORT_TICKET_EVENT, openTicketForm);
    return () => window.removeEventListener(OPEN_LOGAN_SUPPORT_TICKET_EVENT, openTicketForm);
  }, []);

  useEffect(() => {
    const showWorkflowHelp = () => setWorkflowHint(true);
    window.addEventListener(LOGAN_WORKFLOW_ERROR_EVENT, showWorkflowHelp);
    return () => window.removeEventListener(LOGAN_WORKFLOW_ERROR_EVENT, showWorkflowHelp);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (streamTimerRef.current) window.clearInterval(streamTimerRef.current);
      if (recordingTimerRef.current) window.clearTimeout(recordingTimerRef.current);
      if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current?.stop();
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    if (!historyOpen) return;
    const timer = window.setTimeout(() => void loadHistory(historySearch), 250);
    return () => window.clearTimeout(timer);
  }, [historyOpen, historySearch]);

  const revealReply = (reply: AssistantReply, question: string) => {
    if (streamTimerRef.current) window.clearInterval(streamTimerRef.current);
    const messageId = reply.messageId;
    let index = 0;
    setStreamingId(messageId);
    setMessages((current) => [...current, {
      id: messageId, role: "assistant", message: "", question,
      sources: reply.sources, actions: reply.actions, cards: reply.cards,
    }]);
    streamTimerRef.current = window.setInterval(() => {
      index = Math.min(reply.answer.length, index + Math.max(3, Math.ceil(reply.answer.length / 90)));
      setMessages((current) => current.map((message) => message.id === messageId
        ? { ...message, message: reply.answer.slice(0, index), suggestions: index >= reply.answer.length ? reply.suggestions : undefined }
        : message));
      if (index >= reply.answer.length) stopStreaming();
    }, 18);
  };

  const stopStreaming = () => {
    if (streamTimerRef.current) window.clearInterval(streamTimerRef.current);
    streamTimerRef.current = null;
    setStreamingId(null);
  };

  const loadHistory = async (search = historySearch) => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      setConversations(await listConversations(search));
    } catch (_error) {
      setHistoryError("Chat history could not be loaded.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryOpen(false);
    setHistoryMenuId(null);
    setPendingDeleteId(null);
  };

  const openHistory = () => {
    stopStreaming();
    setHistoryMenuId(null);
    setPendingDeleteId(null);
    setHistoryOpen(true);
    setTicketStep(null);
  };

  const selectConversation = async (id: string) => {
    stopStreaming();
    setHistoryLoading(true);
    try {
      const conversation = await getConversation(id);
      setConversationId(conversation.id);
      setConversationTitle(conversation.title);
      setMessages(conversation.messages.length ? conversation.messages : [welcomeMessage]);
      closeHistory();
      setFeedback({});
    } catch (_error) {
      setHistoryError("That conversation could not be opened.");
    } finally { setHistoryLoading(false); }
  };

  const removeConversation = async (id: string) => {
    setDeletingId(id);
    setHistoryError("");
    try {
      await deleteConversation(id);
      if (id === conversationId) {
        const created = await createConversation(getLocale(), brandName);
        setConversationId(created.id);
        setConversationTitle("New conversation");
        setMessages([welcomeMessage]);
      }
      setPendingDeleteId(null);
      setHistoryMenuId(null);
      await loadHistory();
    } catch (_error) {
      setHistoryError("The conversation could not be deleted.");
    } finally {
      setDeletingId(null);
    }
  };

  const editConversationTitle = async (item: AssistantConversationSummary) => {
    const next = window.prompt("Rename this Logan chat", item.title)?.trim();
    if (!next || next === item.title) return;
    try {
      const title = next.slice(0, 190);
      await renameConversation(item.id, title);
      if (item.id === conversationId) setConversationTitle(title);
      setHistoryMenuId(null);
      await loadHistory();
    } catch (_error) {
      setHistoryError("The conversation could not be renamed.");
    }
  };

  const submit = async (text = input) => {
    const question = text.trim();
    if (!question || loading || streamingId) return;
    if (conversationTitle === "New conversation" || /^Logan\s[-—]/i.test(conversationTitle)) {
      setConversationTitle(question.slice(0, 80));
    }

    let activeConversationId = conversationId;
    try {
      if (!activeConversationId) {
        const created = await createConversation(getLocale(), brandName);
        activeConversationId = created.id;
        setConversationId(created.id);
      }
      const optimistic: AssistantMessage = {
        id: `local-${Date.now()}`,
        role: "user",
        message: question,
        pending: true,
      };
      setMessages((current) => [...current, optimistic]);
      setInput("");
      setLoading(true);

      const reply = await sendAssistantMessage(activeConversationId, question, pageContext);
      setMessages((current) => current.map((message) => message.id === optimistic.id ? { ...message, pending: false } : message));
      revealReply(reply, question);
      if (reply.actions?.some((action) => action.type === "start_project_creation")) {
        openContractWorkspace(question);
      }
      if (reply.actions?.some((action) => action.type === "create_support_ticket")) {
        setTicketStep("form");
        setTicketError("");
      }
    } catch (_error) {
      setMessages((current) => [
        ...current.map((message) => message.pending ? { ...message, pending: false } : message),
        {
          id: `error-${Date.now()}`,
          role: "assistant",
          message: "Logan could not respond right now. Please try again in a moment.",
          failed: true,
          question,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = async () => {
    if (loading) return;
    stopStreaming();
    setLoading(true);
    try {
      const created = await createConversation(getLocale(), brandName);
      setConversationId(created.id);
      setConversationTitle("New conversation");
      setMessages([welcomeMessage]);
      setFeedback({});
      closeHistory();
    } catch (_error) {
      setMessages((current) => [...current, {
        id: `error-${Date.now()}`,
        role: "assistant",
        message: "A new chat could not be started. Please try again.",
        failed: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const rateMessage = async (messageId: string, rating: "helpful" | "not_helpful", reason = "", comment = "") => {
    setFeedback((current) => ({ ...current, [messageId]: rating }));
    try {
      await saveAssistantFeedback(messageId, rating, reason, comment);
    } catch (_error) {
      setFeedback((current) => {
        const next = { ...current };
        delete next[messageId];
        return next;
      });
    }
  };

  const submitDetailedFeedback = async () => {
    if (!feedbackMessageId || !feedbackReason) return;
    await rateMessage(feedbackMessageId, "not_helpful", feedbackReason, feedbackComment.trim());
    setFeedbackMessageId(null); setFeedbackReason(""); setFeedbackComment("");
  };

  const prepareTicketDraft = async () => {
    setTicketDrafting(true); setTicketError("");
    try {
      let activeConversationId = conversationId;
      if (!activeConversationId) {
        const created = await createConversation(getLocale(), brandName);
        activeConversationId = created.id; setConversationId(created.id);
      }
      const draft = await draftSupportTicket(activeConversationId, pageContext);
      setTicketSubject(draft.subject); setTicketDescription(draft.description);
    } catch (_error) {
      setTicketError("Logan could not prepare the ticket draft. You can still enter it manually.");
    } finally { setTicketDrafting(false); }
  };

  const retryMessage = (message: AssistantMessage, index: number) => {
    const question = message.question || [...messages.slice(0, index)].reverse().find((item) => item.role === "user")?.message;
    if (question) void submit(question);
  };

  const copyMessage = async (value: string) => {
    try { await navigator.clipboard.writeText(value); } catch (_error) { /* Clipboard may be unavailable. */ }
  };

  const submitSupportTicket = async () => {
    if (ticketSubmitting || ticketSubject.trim().length < 5 || ticketDescription.trim().length < 10) return;
    setTicketSubmitting(true);
    setTicketError("");
    try {
      let activeConversationId = conversationId;
      if (!activeConversationId) {
        const created = await createConversation(getLocale(), brandName);
        activeConversationId = created.id;
        setConversationId(created.id);
      }
      const subject = ticketSubject.trim();
      const description = ticketDescription.trim();
      const reply = await createSupportTicketRequest(
        activeConversationId,
        subject,
        description,
        pageContext
      );
      notifyLoganSupportTicketCreated();
      setMessages((current) => [
        ...current,
        {
          id: `ticket-request-${Date.now()}`,
          role: "user",
          message: `Create support ticket\nSubject: ${subject}\nDescription: ${description}`,
        },
        {
          id: reply.messageId,
          role: "assistant",
          message: reply.answer,
          sources: reply.sources,
          actions: reply.actions,
          suggestions: reply.suggestions,
        },
      ]);
      setTicketStep(null);
      setTicketSubject("");
      setTicketDescription("");
    } catch (error: any) {
      setTicketError(error?.response?.data?.message || "The support ticket could not be created. Please try again.");
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  const historyContent = (
    <>
      <div className="product-assistant__ticket-header">
        <button
          type="button"
          className="product-assistant__ticket-back"
          onClick={closeHistory}
          aria-label={isPage ? "Close chat history" : "Back to chat"}
        >
          {isPage ? <X size={19} /> : <ArrowLeft size={19} />}
        </button>
        <div><strong>Chat history</strong><span>Search and reopen your Logan conversations.</span></div>
      </div>
      <div className="product-assistant__history-body">
        <div className="product-assistant__history-search app-search-field">
          <Search className="app-field-icon" size={16} aria-hidden="true" />
          <input
            value={historySearch}
            onChange={(event) => setHistorySearch(event.target.value)}
            placeholder="Search chats"
            aria-label="Search chat history"
          />
        </div>
        {historyLoading && <div className="product-assistant__empty" role="status">Loading conversations…</div>}
        {historyError && (
          <div className="product-assistant__history-error" role="alert">
            <span>{historyError}</span>
            <button type="button" onClick={() => void loadHistory()}><RefreshCw size={13} /> Retry</button>
          </div>
        )}
        {!historyLoading && !historyError && conversations.length === 0 && <div className="product-assistant__empty">No matching conversations.</div>}
        {!historyLoading && conversations.map((item) => (
          <div className={`product-assistant__history-item${item.id === conversationId ? " is-active" : ""}`} key={item.id}>
            <button type="button" className="product-assistant__history-open" onClick={() => void selectConversation(item.id)} aria-current={item.id === conversationId ? "true" : undefined}>
              <strong title={formatConversationTitle(item.title, brandName)}>{formatConversationTitle(item.title, brandName)}</strong>
              <span title={item.preview}>{item.preview}</span>
              <small>{item.messageCount} messages <i aria-hidden="true">·</i> {formatRelativeDate(item.updatedAt)}</small>
            </button>
            <div className="product-assistant__history-actions">
              <button
                type="button"
                className="product-assistant__history-menu-trigger"
                onClick={() => { setPendingDeleteId(null); setHistoryMenuId((current) => current === item.id ? null : item.id); }}
                aria-label={`Actions for ${formatConversationTitle(item.title, brandName)}`}
                aria-expanded={historyMenuId === item.id}
                title="Conversation actions"
              ><MoreHorizontal size={17} /></button>
              {historyMenuId === item.id && (
                <div className="product-assistant__history-menu">
                  {pendingDeleteId === item.id ? (
                    <div className="product-assistant__delete-confirmation">
                      <strong>Delete this chat?</strong>
                      <div>
                        <button type="button" onClick={() => setPendingDeleteId(null)}>Cancel</button>
                        <button type="button" className="is-danger" disabled={deletingId === item.id} onClick={() => void removeConversation(item.id)}>
                          {deletingId === item.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button type="button" onClick={() => void editConversationTitle(item)}><Pencil size={14} /> Rename</button>
                      <button type="button" className="is-danger" onClick={() => setPendingDeleteId(item.id)}><Trash2 size={14} /> Delete</button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const stopVoiceRecording = () => {
    if (recordingTimerRef.current) window.clearTimeout(recordingTimerRef.current);
    recordingTimerRef.current = null;
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
  };

  const startVoiceRecording = async () => {
    if (recording) return stopVoiceRecording();
    setVoiceError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceError("Voice recording is not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const supportedType = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"]
        .find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = supportedType ? new MediaRecorder(stream, { mimeType: supportedType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        setVoiceError("The recording stopped unexpectedly. Please try again.");
        stopVoiceRecording();
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        if (!mountedRef.current) return;
        setRecording(false);

        const mimeType = recorder.mimeType || supportedType || "audio/webm";
        const audio = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        if (audio.size < 500) {
          setVoiceError("No speech was captured. Please try again.");
          return;
        }

        setTranscribing(true);
        try {
          const extension = mimeType.includes("mp4") ? "m4a" : mimeType.includes("ogg") ? "ogg" : "webm";
          const transcript = await transcribeAssistantAudio(audio, extension);
          if (!mountedRef.current) return;
          setInput(transcript);
          setTranscribing(false);
          await submit(transcript);
        } catch (error: any) {
          if (mountedRef.current) {
            setVoiceError(error?.response?.data?.message || error?.message || "Logan could not understand that recording.");
          }
        } finally {
          if (mountedRef.current) setTranscribing(false);
        }
      };

      recorder.start();
      setRecording(true);
      recordingTimerRef.current = window.setTimeout(stopVoiceRecording, 60_000);
    } catch (error: any) {
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
      setRecording(false);
      setVoiceError(error?.name === "NotAllowedError"
        ? "Microphone permission is required to send a voice message."
        : "The microphone could not be started. Please try again.");
    }
  };

  if (!isPage && location.pathname === "/create-contract") return null;

  return (
    <div className={`product-assistant${isPage ? " product-assistant--page" : ""}${isPage && historyOpen ? " product-assistant--history-open" : ""}`}>
      {isPage && (
        <header className="logan-workspace__toolbar">
          <div className="logan-workspace__identity">
            <span className="logan-workspace__identity-icon" aria-hidden="true"><Sparkles size={18} /></span>
            <div>
              <strong>Logan</strong>
              <span>{brandName} AI Product Guide</span>
            </div>
          </div>
          <div className="logan-workspace__hero-actions">
            <span className="logan-workspace__online"><i /> Logan is online</span>
            <button type="button" className="is-secondary-action" onClick={() => { setTicketStep("form"); setTicketError(""); }} aria-label="Open support"><LifeBuoy size={17} /><span>Support</span></button>
            <button
              type="button"
              className={`is-secondary-action${historyOpen ? " is-active" : ""}`}
              onClick={historyOpen ? closeHistory : openHistory}
              aria-expanded={historyOpen}
              aria-controls="logan-chat-history"
            ><History size={17} /><span>{historyOpen ? "Hide history" : "Show history"}</span></button>
            <button type="button" className="is-secondary-action" onClick={() => void startNewChat()} aria-label="Start a new chat"><Plus size={18} /><span>New chat</span></button>
            <button type="button" className="is-primary" onClick={() => openContractWorkspace()}><FilePlus2 size={17} /><span>Start contract setup</span></button>
          </div>
        </header>
      )}
      {isPage && projectGateMessage && (
        <div className="product-assistant__project-gate" role="alert">{projectGateMessage}</div>
      )}

      {historyOpen && isPage && (
        <aside id="logan-chat-history" className="product-assistant__history-panel" aria-label="Chat history">
          {historyContent}
        </aside>
      )}

      {open && (
        <section className="product-assistant__panel" role={isPage ? "region" : "dialog"} aria-label={`Logan, ${brandName} AI Product Guide`}>
          {isPage && (
            <div className="product-assistant__conversation-context">
              <span>Current conversation</span>
              <strong title={formatConversationTitle(conversationTitle, brandName)}>{formatConversationTitle(conversationTitle, brandName)}</strong>
            </div>
          )}
          {!isPage && <header className="product-assistant__header">
            <div className="product-assistant__identity">
              <div className="product-assistant__avatar" aria-hidden="true">
                <Sparkles size={21} strokeWidth={2.2} />
                <span>AI</span>
              </div>
              <div>
                <div className="product-assistant__title">Logan</div>
                <div className="product-assistant__status">{brandName} AI Product Guide • Support</div>
              </div>
            </div>
            <div className="product-assistant__header-actions">
              <button className="product-assistant__icon-button" onClick={openHistory} title="Chat history" aria-label="Open chat history">
                <History size={18} />
              </button>
              <button className="product-assistant__icon-button" onClick={startNewChat} title="New chat" aria-label="Start new chat">
                <Plus size={19} />
              </button>
              {!isPage && (
                <button className="product-assistant__icon-button" onClick={() => setOpen(false)} title="Close" aria-label="Close Product Guide">
                  <X size={20} />
                </button>
              )}
            </div>
          </header>}
          {!isPage && <div className="product-assistant__notice">
            <span>Logan guides you through {brandName} and can create a support ticket with your confirmation.</span>
            <button type="button" onClick={() => { setTicketStep("form"); setTicketError(""); }}>
              <LifeBuoy size={13} /> Create ticket
            </button>
          </div>}

          {historyOpen && !isPage && (
            <div className="product-assistant__overlay-panel">
              {historyContent}
            </div>
          )}

          {feedbackMessageId && (
            <div className="product-assistant__overlay-panel">
              <div className="product-assistant__ticket-header">
                <button type="button" className="product-assistant__ticket-back" onClick={() => setFeedbackMessageId(null)} aria-label="Close feedback"><X size={19} /></button>
                <div><strong>Help Logan improve</strong><span>Tell us what was missing from this answer.</span></div>
              </div>
              <div className="product-assistant__ticket-body">
                <label htmlFor="logan-feedback-reason">What went wrong?</label>
                <select id="logan-feedback-reason" value={feedbackReason} onChange={(event) => setFeedbackReason(event.target.value)}>
                  <option value="">Select a reason</option><option value="incorrect">Incorrect information</option><option value="missing_steps">Missing steps</option><option value="not_relevant">Not relevant</option><option value="permission_issue">Permission or access issue</option><option value="hard_to_understand">Hard to understand</option>
                </select>
                <label htmlFor="logan-feedback-comment">More details (optional)</label>
                <textarea id="logan-feedback-comment" maxLength={1000} value={feedbackComment} onChange={(event) => setFeedbackComment(event.target.value)} placeholder="What answer would have helped?" />
                <button type="button" className="product-assistant__ticket-primary" disabled={!feedbackReason} onClick={() => void submitDetailedFeedback()}>Send feedback</button>
              </div>
            </div>
          )}

          {ticketStep && (
            <div className="product-assistant__ticket-panel">
              <div className="product-assistant__ticket-header">
                <button
                  type="button"
                  className="product-assistant__ticket-back"
                  onClick={() => ticketStep === "confirm" ? setTicketStep("form") : setTicketStep(null)}
                  aria-label={ticketStep === "confirm" ? "Back to ticket form" : "Close ticket form"}
                >
                  {ticketStep === "confirm" ? <ArrowLeft size={19} /> : <X size={19} />}
                </button>
                <div>
                  <strong>{ticketStep === "confirm" ? "Confirm support ticket" : "Create a support ticket"}</strong>
                  <span>Logan will send this to the {brandName} support team.</span>
                </div>
              </div>

              {ticketStep === "form" ? (
                <div className="product-assistant__ticket-body">
                  <label htmlFor="logan-ticket-subject">Subject</label>
                  <input
                    id="logan-ticket-subject"
                    value={ticketSubject}
                    minLength={5}
                    maxLength={190}
                    onChange={(event) => setTicketSubject(event.target.value)}
                    placeholder="Briefly describe the issue"
                  />
                  <div className="product-assistant__field-count">{ticketSubject.length}/190</div>

                  <label htmlFor="logan-ticket-description">Description</label>
                  <textarea
                    id="logan-ticket-description"
                    value={ticketDescription}
                    minLength={10}
                    maxLength={5000}
                    rows={7}
                    onChange={(event) => setTicketDescription(event.target.value)}
                    placeholder="What happened, what did you expect, and which page were you using?"
                  />
                  <div className="product-assistant__field-count">{ticketDescription.length}/5000</div>
                  {ticketError && <div className="product-assistant__ticket-error">{ticketError}</div>}
                  <button type="button" className="product-assistant__ticket-draft" disabled={ticketDrafting} onClick={() => void prepareTicketDraft()}>
                    <Sparkles size={14} /> {ticketDrafting ? "Preparing draft…" : "Draft from this chat"}
                  </button>
                  <p className="product-assistant__ticket-hint">
                    Do not include passwords, payment card details, private keys, or verification codes.
                  </p>
                  <button
                    type="button"
                    className="product-assistant__ticket-primary"
                    disabled={ticketSubject.trim().length < 5 || ticketDescription.trim().length < 10}
                    onClick={() => setTicketStep("confirm")}
                  >
                    Review ticket
                  </button>
                </div>
              ) : (
                <div className="product-assistant__ticket-body">
                  <div className="product-assistant__ticket-review">
                    <span>Subject</span>
                    <strong>{ticketSubject.trim()}</strong>
                  </div>
                  <div className="product-assistant__ticket-review">
                    <span>Description</span>
                    <p>{ticketDescription.trim()}</p>
                  </div>
                  <div className="product-assistant__ticket-review">
                    <span>Page context</span>
                    <p>{pageContext.route}</p>
                  </div>
                  {ticketError && <div className="product-assistant__ticket-error">{ticketError}</div>}
                  <p className="product-assistant__ticket-hint">
                    The ticket will only be created after you select Confirm and create.
                  </p>
                  <button
                    type="button"
                    className="product-assistant__ticket-primary"
                    disabled={ticketSubmitting}
                    onClick={() => void submitSupportTicket()}
                  >
                    {ticketSubmitting ? "Creating ticket..." : "Confirm and create"}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="product-assistant__messages" aria-live="polite">
            {isWelcomeState && (
              <div className="logan-workspace__welcome">
                <span className="logan-workspace__welcome-icon" aria-hidden="true"><Sparkles size={22} /></span>
                <h1>How can I help you today?</h1>
                <p>Ask about {brandName} workflows, find the right page, or get help completing a task.</p>
                <button type="button" className="logan-workspace__create-project" onClick={() => openContractWorkspace()}>
                  <span><FilePlus2 size={20} /></span>
                  <div><strong>Start contract setup with Logan</strong><small>Open the contract workspace with Logan ready to use the project details from this chat.</small></div>
                  <ArrowRight size={18} />
                </button>
                <div className="logan-workspace__welcome-prompts" aria-label="Suggested questions">
                  {visibleSuggestions.map((suggestion) => (
                    <button key={suggestion} type="button" onClick={() => void submit(suggestion)} disabled={Boolean(streamingId)}>
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!isWelcomeState && messages.map((message, messageIndex) => (
              <div key={message.id} className={`product-assistant__row product-assistant__row--${message.role}`}>
                {isPage && message.role === "assistant" && (
                  <span className="product-assistant__message-avatar" aria-label="Logan"><Sparkles size={15} /></span>
                )}
                <div className={`product-assistant__bubble${message.failed ? " product-assistant__bubble--failed" : ""}`}>
                  {message.role === "assistant"
                    ? <AssistantMessageContent message={message.message} showSender={isPage} />
                    : <span className="product-assistant__message-copy">{message.message}</span>}
                  {message.role === "assistant" && message.cards?.map((card) => (
                    <div className="product-assistant__data-card" key={`${message.id}-${card.title}`}><strong>{card.title}</strong>{card.fields.map((field) => <div key={field.label}><span>{field.label}</span><b>{field.value}</b></div>)}</div>
                  ))}
                  {message.role === "assistant" && message.sources && message.sources.length > 0 && (
                    <div className="product-assistant__sources">
                      <div className="product-assistant__source-label">Product sources</div>
                      {message.sources.map((source) => (
                        <button
                          key={source.id}
                          className="product-assistant__source"
                          disabled={!isSafeInternalRoute(source.route)}
                          onClick={() => isSafeInternalRoute(source.route) && navigate(source.route as string)}
                        >
                          {source.title}{isSafeInternalRoute(source.route) ? " →" : ""}
                        </button>
                      ))}
                    </div>
                  )}
                  {message.role === "assistant" && message.actions?.some((action) => action.type === "create_support_ticket") && (
                    <button
                      type="button"
                      className="product-assistant__inline-ticket"
                      onClick={() => { setTicketStep("form"); setTicketError(""); }}
                    >
                      <LifeBuoy size={14} /> Create support ticket here
                    </button>
                  )}
                  {message.role === "assistant" && message.actions?.filter(
                    (action) => action.type === "navigate" && isSafeInternalRoute(action.route)
                  ).map((action) => (
                    <button
                      key={`${message.id}-${action.route}`}
                      type="button"
                      className="product-assistant__navigate-action"
                      onClick={() => navigate(action.route as string)}
                    >
                      {action.label} <ArrowRight size={14} />
                    </button>
                  ))}
                  {message.role === "assistant" && message.id !== "welcome" && !message.failed && (
                    <div className="product-assistant__feedback">
                      Helpful?
                      <button
                        className={feedback[message.id] === "helpful" ? "is-selected" : ""}
                        aria-label="Helpful response"
                        onClick={() => void rateMessage(message.id, "helpful")}
                      ><ThumbsUp size={14} /></button>
                      <button
                        className={feedback[message.id] === "not_helpful" ? "is-selected" : ""}
                        aria-label="Not helpful response"
                        onClick={() => setFeedbackMessageId(message.id)}
                      ><ThumbsDown size={14} /></button>
                      <button aria-label="Copy answer" title="Copy answer" onClick={() => void copyMessage(message.message)}><Copy size={14} /></button>
                    </div>
                  )}
                  {message.failed && <button type="button" className="product-assistant__retry" onClick={() => retryMessage(message, messageIndex)}><RefreshCw size={13} /> Retry</button>}
                </div>
              </div>
            ))}
            {loading && (
              <div className="product-assistant__row product-assistant__row--assistant">
                {isPage && <span className="product-assistant__message-avatar" aria-label="Logan"><Sparkles size={15} /></span>}
                <div className="product-assistant__bubble">
                  <span className="product-assistant__typing"><span /><span /><span /></span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {!isPage && !loading && visibleSuggestions.length > 0 && (
            <div className="product-assistant__suggestions">
              {visibleSuggestions.map((suggestion) => (
                <button key={suggestion} className="product-assistant__suggestion" onClick={() => void submit(suggestion)}>
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {(recording || transcribing || voiceError) && (
            <div className={`product-assistant__voice-status${voiceError ? " is-error" : ""}`} role="status">
              {recording && <><span className="product-assistant__recording-dot" /> Listening… select Stop when you’re done.</>}
              {transcribing && <><span className="product-assistant__voice-spinner" /> Turning your voice into a Logan question…</>}
              {voiceError && <><span>{voiceError}</span><button type="button" onClick={() => setVoiceError("")} aria-label="Dismiss voice error"><X size={14} /></button></>}
            </div>
          )}

          <form className="product-assistant__composer" onSubmit={handleSubmit}>
            <textarea
              className="product-assistant__input"
              value={input}
              maxLength={1500}
              rows={1}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Ask Logan about ${brandName}...`}
              aria-label={`Ask Logan, the ${brandName} AI Product Guide`}
            />
            <button
              className={`product-assistant__voice${recording ? " is-recording" : ""}`}
              type="button"
              onClick={() => void startVoiceRecording()}
              disabled={transcribing || loading || Boolean(streamingId)}
              aria-label={recording ? "Stop voice recording" : "Ask Logan by voice"}
              title={recording ? "Stop recording" : "Ask by voice"}
            >
              {recording ? <Square size={16} /> : <Mic size={18} />}
            </button>
            <button className="product-assistant__send" type={streamingId ? "button" : "submit"} onClick={streamingId ? stopStreaming : undefined} disabled={streamingId ? false : (!input.trim() || loading)} aria-label={streamingId ? "Stop response" : "Send message"}>
              {streamingId ? <Square size={16} /> : <Send size={18} />}
            </button>
          </form>
        </section>
      )}

      {!open && !isPage && !isLeadWorkspace && (
        <button className={`product-assistant__launcher${workflowHint ? " product-assistant__launcher--attention" : ""}${isLeadWorkspace ? " product-assistant__launcher--compact" : ""}`} onClick={() => navigate("/logan-ai")} aria-label={`Open Logan, the ${brandName} AI Product Guide`} title="Ask Logan">
          <span className="product-assistant__launcher-ring" aria-hidden="true" />
          <span className="product-assistant__launcher-mark" aria-hidden="true">
            <Sparkles size={24} strokeWidth={2.2} />
            <span className="product-assistant__ai-badge">AI</span>
          </span>
        </button>
      )}
    </div>
  );
};

function getLocale() {
  return /^[a-z]{2}(-[A-Z]{2})?$/.test(navigator.language) ? navigator.language : "en-US";
}

function isSafeInternalRoute(route: string | null | undefined): route is string {
  return typeof route === "string" && /^\/(?!\/)[a-zA-Z0-9/_?&=#.-]+$/.test(route);
}

function getPageContext(pathname: string, search = "") {
  const route = `${pathname}${search}`.slice(0, 300);
  const firstSegment = pathname.split("/").filter(Boolean)[0] || "dashboard";
  const searchParams = new URLSearchParams(search);
  const moduleMap: Record<string, string> = {
    "contracts-list": "contracts",
    "create-contract": "contracts",
    "project-details": "projects",
    "pages-projects": "leads",
    "project-requested": "leads",
    "project-ongoing-leads": "leads",
    "leadhistory": "leads",
    "pages-all-leads": "leads",
    "invoices-list": "invoices",
    "team_management": "team",
    "create-your-team": "subcontractors",
    "merchant": "merchant",
    "pages-profile-settings": "profile",
    "support-tickets": "support",
    "dashboard": "dashboard",
    "agent-property-leads": "real-estate",
  };
  const queryId = searchParams.get("id") || searchParams.get("projectId");
  const entityId = queryId?.match(/^\d+$/)?.[0] || pathname.match(/\/(\d+)(?:\/|$)/)?.[1] || "";
  const moduleName = firstSegment === "create-contract" && searchParams.get("type") === "subcontract"
    ? "subcontracts"
    : moduleMap[firstSegment] || firstSegment;
  return { route, module: moduleName, entityId };
}

function getContextualSuggestion(moduleName: string, roleLabel: string) {
  if (roleLabel === "homeowner") {
    const homeownerSuggestions: Record<string, string> = {
      contracts: "How do I review my contract status?",
      leads: "How do I manage my project requests?",
      projects: "Where can I view my project details?",
      "real-estate": "How do I view recommended properties?",
      profile: "How do I update my profile?",
      support: "Show my recent support tickets",
      dashboard: "What can I do from my homeowner dashboard?",
    };
    return homeownerSuggestions[moduleName] || "How do I manage my projects?";
  }
  const suggestions: Record<string, string> = {
    contracts: "What can I do on this contract page?", leads: "Show my recent lead status",
    invoices: "Explain this invoice page", team: "Show my team summary",
    merchant: "Check my merchant status", profile: "Help me complete my profile",
    support: "Show my recent support tickets", dashboard: "Summarize what I can do here",
  };
  return suggestions[moduleName] || "What can I do on this page?";
}

function getVisibleSuggestions(suggestions: string[], roleLabel: string) {
  const unique = Array.from(new Set(suggestions.filter(Boolean)));
  if (roleLabel !== "homeowner") return unique.slice(0, 4);

  const homeownerOnly = unique.filter((suggestion) => !isContractorOnlySuggestion(suggestion));
  const defaults = [
    "How do I manage my projects?",
    "Where can I find suggested contractors?",
    "How do I create a support ticket?",
  ];
  return Array.from(new Set([...homeownerOnly, ...defaults])).slice(0, 4);
}

function isContractorOnlySuggestion(suggestion: string) {
  const value = suggestion.toLowerCase();
  return /\b(create|start|make|prepare|set up|setup)\b.*\b(contract|subcontract)\b/.test(value)
    || /\b(subcontract|team permissions|team management|merchant|kyc|invoice|subscription|leads pro|accepted lead|requested lead|lead history)\b/.test(value);
}

function formatRelativeDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently";
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

function formatConversationTitle(title: string, brandName: string) {
  const cleanTitle = title.replace(/\s+Current contract context:[\s\S]*$/i, "").trim();
  if (/^(logan\s*[-—]\s*)?relibuild product guide$/i.test(cleanTitle)) {
    return `Logan — ${brandName} Product Guide`;
  }
  return cleanTitle || "New conversation";
}

function AssistantMessageContent({ message, showSender }: { message: string; showSender: boolean }) {
  const [firstLine, ...remainingLines] = message.split("\n");
  const hasShortHeading = remainingLines.some((line) => line.trim()) && firstLine.trim().length > 0 && firstLine.trim().length <= 72;
  return (
    <div className="product-assistant__message-content">
      {showSender && <span className="product-assistant__message-sender">Logan</span>}
      {hasShortHeading && <strong className="product-assistant__message-title">{firstLine.trim()}</strong>}
      <span className="product-assistant__message-copy">
        {hasShortHeading ? remainingLines.join("\n").trim() : message}
      </span>
    </div>
  );
}

export default ProductAssistantWidget;
